/**
 * sessionExpiredBus — a tiny, decoupled event bus for "the session could not be
 * recovered" signals (SESSION PR-C2b-A).
 *
 * WHY: low-level transport code (the axios response interceptor) must NOT
 * hard-redirect to `/login` via `window.location.href`. That full-document
 * navigation silently destroys unsaved work and bypasses both AuthContext and
 * the unsaved-changes registry. Instead, the interceptor dispatches a
 * session-expired signal here, and AuthContext owns the final, dirty-aware
 * transition (graceful local sign-out when clean; block + preserve work when a
 * tracked form is dirty).
 *
 * Decoupled on purpose: AuthContext imports `@/lib/api`, so `@/lib/api` must NOT
 * import AuthContext (circular). Both sides depend only on this bus, which is a
 * thin wrapper over a DOM CustomEvent.
 *
 * SECURITY: no token/cookie/CSRF/Authorization value is ever carried on this
 * event — only a coarse `reason` string.
 */

export const SESSION_EXPIRED_EVENT = 'amuster:session-expired';

export type SessionExpiredReason =
  | 'refresh_failed'
  | 'refresh_csrf_failed'
  | 'refresh_timeout'
  | 'peer_refresh_failed';

export interface SessionExpiredDetail {
  reason: SessionExpiredReason;
}

/**
 * Dispatch a session-expired signal. Safe to call from non-React transport code
 * and from SSR/no-DOM contexts (no-op when `window` is unavailable).
 */
export function dispatchSessionExpired(reason: SessionExpiredReason): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<SessionExpiredDetail>(SESSION_EXPIRED_EVENT, { detail: { reason } }),
  );
}

/**
 * Subscribe to session-expired signals. Returns an unsubscribe function.
 * The handler receives the coarse reason (never any token/secret).
 */
export function onSessionExpired(
  handler: (detail: SessionExpiredDetail) => void,
): () => void {
  if (typeof window === 'undefined') return () => {};
  const listener = (e: Event): void => {
    const detail = (e as CustomEvent<SessionExpiredDetail>).detail;
    handler(detail ?? { reason: 'refresh_failed' });
  };
  window.addEventListener(SESSION_EXPIRED_EVENT, listener);
  return () => window.removeEventListener(SESSION_EXPIRED_EVENT, listener);
}
